// services/launchToken.service.js
//
// Launch-token handoff between the Telegram Mini App and the system browser.
//
//   TMA  ── POST /auth/launch-token/issue ───────────► returns { token, expires_in }
//   TMA  ── openExternalLink(`/launch?t=<token>`) ──► system browser opens the URL
//   PWA  ── POST /auth/launch-token/exchange ──────► sets Sanctum session cookie
//
// The same launch-token machinery also powers "act as a member" (operator
// impersonation, PWA-only):
//
//   PWA  ── POST /auth/launch-token/act-as {membershipId} ─► { token, expires_in }
//   PWA  ── POST /auth/launch-token/exchange {token} ─────► session BECOMES target
//   PWA  ── POST /auth/launch-token/leave ────────────────► session RESTORES operator
//
// act-as mints a token scoped to a target membership; exchange swaps the
// Sanctum session to that member; leave reverses it. See the FE plan
// `_bmad-output/planning-artifacts/act-as-impersonation-fe-plan-2026-07-05.md`.
//
// Both endpoints go through `authClient`, the canonical Sanctum-aware axios
// flavor (see comment block in src/httpConfig/config/axios.js — single base
// instance is load-bearing for session-cookie hygiene). Do NOT swap to bare
// fetch; that fragments the session cookie surface.
//
// Backend contract: see _bmad-output/implementation-artifacts/spec-telegram-launch-flow.md
// Section Design Notes / Backend contract.

import { authClient } from '@/httpConfig/config/axios.js';

/**
 * Ask the backend for a single-use launch token.
 *
 * Requires an active Sanctum session — typically established by the TMA's
 * /auth/telegram bootstrap from `init_data`. Anonymous callers get 401.
 *
 * @returns {Promise<{ token: string, expires_in: number }>}
 * @throws  {{ status: number, message: string }}
 */
export async function issue() {
  try {
    const response = await authClient.post('/auth/launch-token/issue');
    const token = response.data?.token;
    const expiresIn = response.data?.expires_in ?? 60;

    if (!token) {
      throw { status: response.status || 502, message: 'Empty token in issue response' };
    }

    return { token, expires_in: expiresIn };
  } catch (error) {
    if (error && typeof error === 'object' && 'status' in error && !('response' in error)) {
      // Already shaped (e.g. the throw above)
      throw error;
    }
    const status = error?.response?.status ?? 0;
    const message = error?.response?.data?.message || error?.message || 'Failed to issue launch token';
    throw { status, message };
  }
}

/**
 * Exchange a launch token for a Sanctum session cookie. On success the backend
 * responds 204 with Set-Cookie: laravel_session + XSRF-TOKEN.
 *
 * Public endpoint — no prior session required. The unguessable single-use
 * token is the entire credential.
 *
 * @param {{ token: string }} params
 * @returns {Promise<void>}
 * @throws  {{ status: number, message: string }}
 */
export async function exchange({ token }) {
  if (!token) {
    throw { status: 400, message: 'token is required' };
  }

  try {
    await authClient.post('/auth/launch-token/exchange', { token });
  } catch (error) {
    const status = error?.response?.status ?? 0;
    const message = error?.response?.data?.message || error?.message || 'Failed to exchange launch token';
    throw { status, message };
  }
}

/**
 * Shape a launch-token error the same way issue()/exchange() do:
 * `{ status, message }`, reading `errors[0].detail` (JSON:API envelope) first,
 * then `message`, then the axios error message. Network failures → status 0.
 */
function shapeLaunchTokenError(error, fallback) {
  const status = error?.response?.status ?? 0;
  const message =
    error?.response?.data?.errors?.[0]?.detail ||
    error?.response?.data?.message ||
    error?.message ||
    fallback;
  return { status, message };
}

/**
 * Mint a single-use launch token that impersonates a target membership
 * ("act as a member"). Operator-only — the backend validates the pick against
 * the caller's act-as scope and rejects out-of-scope / non-impersonatable
 * targets. Pair immediately with exchange() (60s, single-use), then hard-reload.
 *
 * @param {{ membershipId: string|number }} params
 * @returns {Promise<{ token: string, expires_in: number }>}
 * @throws  {{ status: number, message: string }} — 400 bad id · 401 unauth ·
 *          403 out-of-scope/not-impersonatable · 409 target has no owning user
 */
export async function actAs({ membershipId }) {
  if (!membershipId && membershipId !== 0) {
    throw { status: 400, message: 'membershipId is required' };
  }

  try {
    const response = await authClient.post('/auth/launch-token/act-as', { membershipId });
    const token = response.data?.token;
    const expiresIn = response.data?.expires_in ?? 60;

    if (!token) {
      throw { status: response.status || 502, message: 'Empty token in act-as response' };
    }

    return { token, expires_in: expiresIn };
  } catch (error) {
    if (error && typeof error === 'object' && 'status' in error && !('response' in error)) {
      // Already shaped (e.g. the throw above / the missing-id guard)
      throw error;
    }
    throw shapeLaunchTokenError(error, 'Failed to mint act-as token');
  }
}

/**
 * Leave an impersonated session and restore the operator's own session. The
 * operator is recorded server-side (no client id needed). Idempotent —
 * responds 204 even when not impersonating.
 *
 * @returns {Promise<void>} resolves on 204
 * @throws  {{ status: number, message: string }} on network/other failure
 */
export async function leave() {
  try {
    await authClient.post('/auth/launch-token/leave');
  } catch (error) {
    throw shapeLaunchTokenError(error, 'Failed to leave impersonated session');
  }
}
